import { Controller, useForm } from 'react-hook-form';
import React from 'react';
import { Select } from '../ui';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import SaveFooterLayout from '../../layouts/SaveFooterLayout';
import LanguageSelector from '../ui/inputs/LanguageSelector/LanguageSelector';
import TimezoneSelect, { allTimezones } from 'react-timezone-select';
import { formatDate } from '@progress/kendo-intl';
import { useTheme } from '@material-ui/core/styles';
import { DateFormatEnum } from '../../store/user/types.d';

const dateFormatOptions = [
  { id: DateFormatEnum.DMY12, label: 'day/month/year 12h' },
  { id: DateFormatEnum.DMY24, label: 'day/month/year 24h' },
  { id: DateFormatEnum.MDY12, label: 'month/day/year 12h' },
  { id: DateFormatEnum.MDY24, label: 'month/day/year 24h' },
  { id: DateFormatEnum.YMD12, label: 'year/month/day 12h' },
  { id: DateFormatEnum.YMD24, label: 'year/month/day 24h' },
]; // https://github.com/telerik/kendo-intl/blob/master/docs/date-formatting/index.md#standard

interface FormFields {
  language: string | null;
  timezone: string;
  date_format: string;
}

interface Props {
  formData: FormFields;
  onSave: (data: FormFields) => void;
  onCancel?: () => void;
}

const UserSettingsForm: React.FC<Props> = ({ formData, onSave, onCancel }) => {
  const { handleSubmit, control } = useForm<FormFields>();
  const { t, i18n } = useTranslation();
  const theme = useTheme();

  const form = (
    <form onSubmit={handleSubmit(onSave)} className="flex flex-col mobileCtrlProfileForm" style={{ flexGrow: 1 }}>
      <StyledFormContent style={{ flexGrow: 1 }}>
        <div className="flex flex-col mx-10" style={{ width: '24rem' }}>
          <Controller
            name="language"
            control={control}
            defaultValue={formData.language ?? i18n.language}
            render={({ onChange, value }) => (
              <StyledLanguageWrapper className="flex flex-col profileFormLanguageWrapper">
                <div className="font-normal text-sm labelSelect">{t('Language')}</div>
                <LanguageSelector value={value} onChange={onChange} />
              </StyledLanguageWrapper>
            )}
          />
          <Controller
            name="timezone"
            control={control}
            defaultValue={formData.timezone}
            render={({ onChange, value }) => (
              <div className="profileFormTimezoneWrapper">
                <div className="font-normal text-sm labelSelect" color={theme.palette.text.primary}>
                  {t('Timezone')}
                </div>
                <TimezoneSelect
                  value={value}
                  onChange={(timezone) => {
                    onChange(timezone.value);
                  }}
                  timezones={{ ...allTimezones, 'etc/gmt+12': 'Anywhere on Earth' }}
                  className="timezoneSelectWrapper"
                />
              </div>
            )}
          />
          <Controller
            name="date_format"
            control={control}
            defaultValue={formData.date_format}
            render={({ onChange, value }) => (
              <div className="profileFormDatetimeWrapper">
                <Select
                  label={t('Date format')}
                  value={value}
                  defaultItem={{ id: '', label: t('System default') }}
                  options={dateFormatOptions.map(({ id, label }) => ({
                    id,
                    label: id ? `${label} (${formatDate(new Date(), id)})` : label,
                  }))}
                  onChange={(dateFormat) => onChange(dateFormat)}
                />
              </div>
            )}
          />
        </div>
      </StyledFormContent>
    </form>
  );

  return (
    <SaveFooterLayout onSave={handleSubmit(onSave)} onCancel={onCancel}>
      {form}
    </SaveFooterLayout>
  );
};

const StyledFormContent = styled.div`
  display: flex;
  margin: 0 auto 4% auto;
  max-width: 930px;

  .k-widget.k-dropdown {
    width: 24rem;
  }

  label,
  .labelSelect {
    margin-top: 15px;
    margin-bottom: 5px;
  }
  .helperText {
    height: auto;
  }
`;

const StyledLanguageWrapper = styled.div`
  .k-dropdown-wrap {
    padding-top: 5px;
    padding-bottom: 4px;
    background-color: #fff !important;
    border: 1px solid #afb0b1 !important;
  }
`;

export default UserSettingsForm;
